import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react"
import API from "../../../services/API"
import Loader from "../../Tools/Loader";
import Error from "./Error";
import '../../../css/quiz.css';
import CheckIcon from '@mui/icons-material/Check';
import CrossIcon from '@mui/icons-material/Close';
import NextButton from '../../Study/NextButton';
import { txt } from "../../../services/translation";

export default function Quiz({section}){

    const [error, setError] = useState(false)
    const [quiz, setQuiz] = useState(null)
    const [index, setIndex] = useState(0)
    const [completed, setCompleted] = useState(section.completed)

    useEffect(() => {

        API.get(`/study/sections/${section.id}/quiz`)
            .then(({data}) => {

                if(data.questions && data.questions?.length > 0){
                    return setQuiz(data)
                }else{
                    return setError(true)
                }
                
            })
            .catch(e => setError(true))

        if(section.completed) setCompleted(true)

        return () => {
            setError(false)
            setQuiz(null)
            setIndex(0)
            setCompleted(false)
        }

    }, [section])


    const handleSubmit = question => {

        API.post(`/study/answers`, question)
            .then(r => console.log(r.data))
            .catch(e => console.log(e?.response?.data))

        let copy = {...quiz};
        let questions = [...copy.questions];
        questions[index] = question;
        copy.questions = questions;
        return setQuiz(copy);
    }

    const restart = () => {
        if(completed) setCompleted(false);

        let copy = {...quiz};
        copy.questions = copy.questions.map(q => {
            let nq = {...q};
            delete nq.answers;
            delete nq.correct;
            return nq;
        })
        setQuiz(copy);
        return setIndex(0);
    }


    if(error) return <Error/>

    if(!quiz) return <Loader/>

    if(completed) return <Completed onRestart={restart} />

    if(!quiz.questions[index]) return <Finish quiz={quiz} onRestart={restart} />

    return <div className='quiz'>

        {quiz.questions.map((q,i) => 
            <Question 
                question={q} 
                show={index === i}
                index={i}
                count={quiz.questions.length}
                onNext={() => setIndex(index + 1 )}
                onSubmit={question => handleSubmit(question)}
                submitted={q.answers ? true : false}
                key={q.id}
            />
        )}

    </div>

}



function Question({question, show, index, count, onNext, onSubmit, submitted}){

    const [answers, setAnswers] = useState([])

    const toggle = answer => {
        let copy = [...answers];
        const idx = answers.findIndex(a => a.id === answer.id);
        if(idx < 0){
            copy.push(answer);
        }else{
            copy.splice(idx, 1)
        }
        return setAnswers(copy)
    }

    const handleSubmit = () => {
        let copy = {...question};
        copy.correct = isCorrect;
        copy.answers = answers;
        return onSubmit(copy);
    }

    const isCorrect = question.choices
        .map(c => c.correct ? c.id : '').sort((a,b) => a > b ? 1 : -1).join('') 
        === answers.map(a => a.id).sort((a,b) => a > b ? 1 : -1).join('');

    const correctAnswers = question.choices
        .map((c, i) => { return {...c, letter: alphabet[i]} })
        .filter(c => c.correct)
        .map(c => c.letter)

    const correctAnswersText = correctAnswers.length > 1 ? 
        <span>{txt('correctAnswers')} <b>{correctAnswers.join(', ')}</b>.</span> :
        <span>{txt('correctAnswer')} <b>{correctAnswers[0]}</b>.</span>;


    const choices = useMemo(() => (question?.choices || [])
        .sort((a,b) => a.rank - b.rank)
        .map((c,i) => {
            return {...c, letter: alphabet[i] || '?'}
        })
    , [question])

    return !show ? <></> : <Stack spacing={2}>
    
        <Typography variant='span'>{txt('questionCounter', { index: index + 1, count })}</Typography>

        <Typography variant='h4'>{question.title}</Typography>

        {question.picture && 
            <Box>
                <img 
                    src={question.picture.url}
                    alt={question.picture.name}
                    height={250}
                />
            </Box>
        }

        <Typography variant='span'>{txt('chooseAllApplicable')}</Typography>


        {choices.map(c => 
            <Choice 
                choice={c}
                selected={answers.findIndex(a => a.id === c.id) >= 0}
                onClick={() => toggle(c)}
                submitted={submitted}
                key={c.id} 
            />    
        )}

        {submitted && 
            (isCorrect ? 
                <Alert severity="success">
                    <b>{txt('correctAnswerText')}</b> {question.explanation}
                </Alert>
                : 
                <Alert severity="error">
                    <b>{txt('incorrectAnswerText')}</b> {correctAnswersText} {question.explanation}
                </Alert>
            )
        }

        <Stack direction='row' justifyContent='end' sx={{mt: 1}}>

            {(!submitted && answers.length > 0) &&
                <Button variant='contained' onClick={handleSubmit}>
                    {txt('validate')}
                </Button>
            }

            {submitted &&
                <Button variant='outlined' onClick={onNext}>
                    {txt('continue')}
                </Button>
            }

        </Stack>

    </Stack>
}



function Choice({choice, index, selected, onClick, submitted}){

    return <div 
        className={'quiz-choice' + (selected ? '-selected' : '')}
        onClick={onClick}
    >

        <div className='quiz-letter'>
            <span>{choice.letter}</span>
        </div>

        <div className='quiz-choice-text'>

            <p>{choice.title}</p>

            {(submitted && choice.explanation) &&
                <p><i>{choice.explanation}</i></p>
            }

        </div>

        {submitted && <div>
            
            {choice.correct && <CheckIcon color='success' sx={{m: 1}} />}

            {(selected && !choice.correct) &&
                <CrossIcon color='error' sx={{m: 1}} />
            }

        </div>}

    </div>
}


function Finish({quiz, onRestart}){

    const score = Math.round(
        quiz.questions.filter(q => q.correct).length 
        / quiz.questions.length
        * 100
    );

    return <Stack direction='column' alignItems='center' sx={{ textAlign: 'center' }}>

        <h3>{txt('quizCompleteTitle')}</h3>

        {score >= quiz.passMark ? 
            <>
                <Alert severity='success' sx={{ mb: 2 }}>
                    {txt('quizPassMessage', { score: score })}
                </Alert>

                <NextButton data={{ quizScore: score }} />
            </>
        :
            <>
                <Alert severity='warning' sx={{ mb: 2 }}>
                    {txt('quizFailMessage', { score: score })}
                </Alert>

                <Button variant='contained' onClick={onRestart}>
                    {txt('restartButton')}
                </Button>
            </>
        }
  </Stack>

}


function Completed({onRestart}){

    return <Stack direction='column' alignItems='center' sx={{ textAlign: 'center' }}>

        <Alert severity='success' sx={{ mb: 2 }}>
            {txt('quizSuccessMessage')}
        </Alert>

        <Stack direction='row' spacing={2}>

            <Button variant='outlined' onClick={onRestart}>
                {txt('restartButton')}
            </Button>

            <NextButton />

        </Stack>

    </Stack>
}




const alphabet = ["A","B","C","D","E","F","G","H","I","J","K","L","M","N","O","P","Q","R","S","T","U","V","W","X","Y","Z"];